import { useState, useCallback, useRef } from 'react';
import api from '../../../services/api';

export const useMessages = (currentUserId) => {
  const [messages, setMessages] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const cacheRef = useRef(null);
  const cacheTimeRef = useRef(0);

  const fetchMessages = useCallback(async (forceRefresh = false, clients = []) => {
    if (!currentUserId) return;
    
    // Cache de 15 secondes pour les messages
    const CACHE_DURATION = 15000;
    const now = Date.now();
    
    if (!forceRefresh && cacheRef.current && (now - cacheTimeRef.current < CACHE_DURATION)) {
      setMessages(cacheRef.current.messages);
      setUnreadMessages(cacheRef.current.unreadMessages);
      return;
    }

    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const { data } = await api.get(`/messages/${currentUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal
      });

      // Enrichir les messages avec les noms des clients
      const enrichedMessages = data.map(msg => {
        const client = clients.find(c => c.id === msg.client_id);
        return {
          ...msg,
          client_name: client ? `${client.first_name} ${client.last_name}` : 'Client inconnu'
        };
      });

      const unread = enrichedMessages.filter(msg => !msg.is_read && msg.recipient_id === currentUserId);

      setMessages(enrichedMessages);
      setUnreadMessages(unread);
      
      // Mettre en cache
      cacheRef.current = { messages: enrichedMessages, unreadMessages: unread };
      cacheTimeRef.current = now;
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('Erreur messages:', err);
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [currentUserId]);

  const sendMessage = useCallback(async (messageData) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await api.post('/messages/create', messageData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Invalider le cache
      cacheRef.current = null;
      return data;
    } catch (err) {
      console.error('Erreur envoi message:', err);
      return null;
    }
  }, []);

  const markAsRead = useCallback(async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      await api.post('/messages/mark-as-read', 
        { client_id: clientId }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => 
        prev.map(msg => 
          msg.client_id === clientId ? { ...msg, is_read: true } : msg
        )
      );
      setUnreadMessages(prev => 
        prev.filter(msg => msg.client_id !== clientId)
      );
      
      // Mettre à jour le cache
      if (cacheRef.current) {
        cacheRef.current.messages = cacheRef.current.messages.map(msg =>
          msg.client_id === clientId ? { ...msg, is_read: true } : msg
        );
        cacheRef.current.unreadMessages = cacheRef.current.unreadMessages.filter(
          msg => msg.client_id !== clientId
        );
      }
    } catch (err) {
      console.error('Erreur marquage lu:', err);
    }
  }, []);

  return {
    messages,
    unreadMessages,
    loading,
    fetchMessages,
    sendMessage,
    markAsRead
  };
};
